import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { convertLLCAddressToCloserAddress } from '@core/util/address';
import { createId, createRandomNumber } from '@core/util/file';
import { centsToDollars } from '@core/util/money';
import { MortgageSourceTag, MortgageTag } from '@core/util/tags';
import { isSet, sanitizePropertyValues } from '@core/util/value';
import {
	FinancialInstitutionTypes,
	LenderData,
	MortgageeType,
} from '@models/lender';
import { Matter, MatterPropertyDetails, MatterTitle } from '@models/matter';
import { MortgageDocumentSource } from '@models/matter-file';
import {
	FundingType,
	InsuranceAmount,
	InterestRateType,
	MatterMortgage,
	MatterMortgageInstructionSource,
	MatterMortgageType,
	SYNC_FLOW_TYPES,
	TransactionType,
} from '@models/mortgage';
import {
	CloserSyncInstruction,
	CloserSyncTransaction,
} from '@models/mortgages/closer-sync-instruction';
import {
	ClientMappings,
	MortgageTransactionSource,
	MortgageTransactionStatus,
} from '@models/mortgages/mortgage-base';
import { TrustAccount } from '@models/trustAccount';
import { MatterMortgagesRepoService } from '@repos/businesses/sales/mortgages.service';
import { MatterTitlesRepoService } from '@repos/matter-titles/matter-titles-repo.service';
import { MatterRepo } from '@repos/matter/matter.repo';
import { MatterFilesRepoService } from '@repos/sale-files/saleFilesRepo';
import { SnackbarService } from '@services/ui/snackbar.service';
import { format, parse } from 'date-fns';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { CloserSyncInstructionsAPIService } from 'src/app/shared/api-repos/mortgages/closer-sync-instructions.api.service';
import { AssignToMatterResponse } from '../llc.service';
import { CloserSyncTransactionRepoService } from '../repos/closerSyncTransactionRepo';
import { buildMortgageFees, getPriority } from './utils';

export interface UpdateFields {
	purchasePrice: boolean;
	closingDate: boolean;
	address: boolean;
	legalDescription: boolean;
	mappings?: ClientMappings;
	guarantors: string[];
	useTrustAccountOnInstructions: boolean;
}

const InsuranceAmountMapping = {
	full_insurance_amount: InsuranceAmount.FULL,
	see_standard_charge_terms: InsuranceAmount.STANDARD_CHARGE,
};

@Injectable({
	providedIn: 'root',
})
export class CloserSyncRepoService {
	constructor(
		private db: AngularFirestore,
		private mortgageRepoService: CloserSyncTransactionRepoService,
		private matterMortgageRepo: MatterMortgagesRepoService,
		private matterFilesRepoService: MatterFilesRepoService,
		private matterRepo: MatterRepo,
		private matterTitlesRepo: MatterTitlesRepoService,
		private snackbarService: SnackbarService,
		private closerSyncInstructionsAPIService: CloserSyncInstructionsAPIService,
	) {}

	/**
	 * Deletes a matter mortgage and unassign it from Closer Sync mortgage, as well as deleting all uploaded files related to the mortgage from the matter.
	 *
	 * @param matterMortgageId - The ID of the matter mortgage to delete.
	 * @param matterId - The ID of the matter associated with the mortgage.
	 * @param transactionId - The ID of the transaction associated with the mortgage.
	 * @returns A Promise that resolves when the deletion is complete.
	 * @throws If there is an error deleting the matter mortgage.
	 */
	deleteMatterMortgage = async (
		matterMortgageId: string,
		matterId: string,
		transactionId: string,
	): Promise<void> => {
		try {
			const matterMortgages = await this.matterMortgageRepo
				.All({ matterId: matterId })
				.pipe(take(1))
				.toPromise();
			const uploadedFiles = await this.matterFilesRepoService
				.All({ matterId: matterId })
				.pipe(take(1))
				.toPromise();

			const batch = this.db.firestore.batch();
			const ccRef = this.mortgageRepoService.DocPath(transactionId);
			batch.update(this.db.firestore.doc(ccRef), {
				matterId: null,
				matterMortgageId: null,
				matterName: null,
				matterNumber: null,
				clientMappings: [],
			});

			matterMortgages.reduce((batch, x) => {
				if (x.id === matterMortgageId) {
					const ref = this.matterMortgageRepo.DocPath(x.id, {
						matterId: matterId,
					});
					batch.delete(this.db.firestore.doc(ref));
				}
				return batch;
			}, batch);

			uploadedFiles
				.filter(
					(x) =>
						x.systemTags?.includes(
							MortgageSourceTag(MortgageDocumentSource.LENDER),
						) && x.systemTags?.includes(MortgageTag(matterMortgageId)),
				)
				.forEach((file) => {
					const ref = this.matterFilesRepoService.DocPath(file.id, {
						matterId: matterId,
					});
					batch.delete(this.db.firestore.doc(ref));
				});

			await batch.commit();
		} catch (err) {
			this.snackbarService.openSnackBar({
				text: 'Unable to unassign Closer SYNC mortgage',
				type: 'error',
			});
			LogRocket.error(
				'Unable to delete matter mortgage and unassign from Closer SYNC mortgage',
				{},
				err,
			);
			throw err;
		}
	};

	/**
	 * Assigns a matter to a mortgage transaction.
	 *
	 * @param transactionId - The ID of the transaction.
	 * @param latestVersionId - The ID of the current assigned version.
	 * @param matterId - The ID of the matter to assign.
	 * @returns A promise that resolves to an AssignToMatterResponse object.
	 */
	assignMatterToMortgage = async (
		transaction: CloserSyncTransaction,
		matterId: string,
		updateFields: UpdateFields,
		primaryTitleId: string,
	): Promise<AssignToMatterResponse> => {
		try {
			const batch = this.db.firestore.batch();

			const transactionDocPath = this.mortgageRepoService.DocPath(
				transaction?.instructionId,
			);

			const instructionDoc = await this.mortgageRepoService
				.getInstructionByVersionId(
					transaction?.instructionId,
					transaction?.latestVersionId,
				)
				.toPromise();

			if (!instructionDoc.exists) {
				throw new Error(`Transaction not found: ${transaction?.instructionId}`);
			}

			const instruction = instructionDoc.data() as CloserSyncInstruction;

			const matterMortgage: MatterMortgage =
				await this.buildCloserSyncMortgages(
					transaction,
					instruction,
					updateFields,
				);

			const updatedMatter: Partial<Matter> = {};

			if (updateFields?.closingDate) {
				updatedMatter.closingDate = instruction.closingDate;
			}

			if (updateFields?.purchasePrice) {
				updatedMatter.purchasePrice = instruction.purchasePrice;
			}

			if (updateFields?.address) {
				const propertyDetails: Partial<MatterPropertyDetails> = {
					address: convertLLCAddressToCloserAddress(
						instruction.properties[0].address,
					),
				};
				updatedMatter.propertyDetails =
					propertyDetails as MatterPropertyDetails;
			}

			if (updatedMatter && Object.keys(updatedMatter).length > 0) {
				batch.set(
					this.db.firestore.doc(this.matterRepo.DocPath(matterId)),
					updatedMatter,
					{ merge: true },
				);
			}

			if (updateFields?.legalDescription) {
				const title: MatterTitle = {
					primary: true,
					legalDescription: instruction.properties[0].legalDescription,
				};
				if (primaryTitleId) {
					batch.update(
						this.db.firestore.doc(
							this.matterTitlesRepo.DocPath(primaryTitleId, { matterId }),
						),
						title,
					);
				} else {
					batch.set(
						this.db.firestore.doc(
							this.matterTitlesRepo.DocPath(createId(), { matterId }),
						),
						title,
					);
				}
			}

			const matterMortgageId = transaction?.matterMortgageId ?? createId();
			const matterMortgageDocPath = this.matterMortgageRepo.DocPath(
				matterMortgageId,
				{ matterId },
			);

			batch.set(this.db.firestore.doc(matterMortgageDocPath), matterMortgage);

			batch.update(this.db.firestore.doc(transactionDocPath), {
				matterId,
				matterMortgageId: matterMortgageId,
				currentAssignedVersionId: transaction?.latestVersionId,
				clientMappings: updateFields?.mappings ?? [],
			});

			await batch.commit();

			try {
				await this.closerSyncInstructionsAPIService
					.downloadAndSaveInstructionDocument(transaction.instructionId)
					.toPromise();
			} catch (err) {
				LogRocket.error(
					'Unable to download and save instruction document',
					err,
				);
			}

			return {
				matterId,
				matterMortgageId,
				transactionId: transaction?.instructionId,
				source: MortgageTransactionSource.CLOSER_SYNC,
			};
		} catch (err) {
			console.log('err', err);
			LogRocket.error(
				'Unable to assign matter to Closer SYNC mortgage',
				{},
				err,
			);
			return null;
		}
	};

	buildCloserSyncMortgages = async (
		transaction: CloserSyncTransaction,
		instruction: CloserSyncInstruction,
		updatedFields: UpdateFields,
	): Promise<MatterMortgage> => {
		let result: MatterMortgage = null;

		try {
			const sourceId = createRandomNumber();

			result = {
				...this.buildMatterMortgage(
					transaction,
					instruction,
					updatedFields.useTrustAccountOnInstructions,
				),
				relatedMortgageId: transaction?.instructionId,
				numericId: sourceId,
				guarantors: updatedFields.guarantors,
				status: transaction?.status,
			} as MatterMortgage;
		} catch (err) {
			LogRocket.error('Unable to build Closer Sync mortgages', {}, err);
			console.error('Unable to build Closer Sync mortgages', err);
		} finally {
			return result;
		}
	};

	tryGetDate = (date: string): string => {
		if (!isSet(date)) return null;

		let result = null;

		try {
			const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
			if (parsedDate) {
				result = format(parsedDate, 'yyyy-MM-dd');
			}
		} catch (err) {
			console.log('tryGetDate', date, err);
		}

		if (result) {
			return result;
		}

		try {
			const _date = new Date(date);
			if (_date.getDate()) {
				result = format(_date, 'yyyy-MM-dd');
			}
		} catch (err) {
			console.log('tryGetDate', date, err);
		}

		return result;
	};

	buildTrustAccount = (ta) => {
		const trustAccountId = createId();
		const trustAccount = {
			id: trustAccountId,
			address: ta?.financialInstitution?.address,
			nickname: '',
			payeeName: '',
			institutionId: ta?.financialInstitution?.institution,
			institutionName: ta?.financialInstitution?.name,
			accountNumber: ta?.financialInstitution?.account,
			last3DigitsAccountNumber: ta?.financialInstitution?.accountDisplayText,
			transit: ta?.financialInstitution?.transit,
			institutionNumber: ta?.financialInstitution?.institution,
			institutionShortName: '',
			voidCheque: '',
		};
		if (!trustAccount?.accountNumber) {
			return null;
		}
		return trustAccount;
	};

	buildMatterMortgage = (
		transaction: CloserSyncTransaction,
		instruction: CloserSyncInstruction,
		useTrustAccountOnInstructions?: boolean,
	): Partial<MatterMortgage> => {
		const transactionType = this.getTransactionType(instruction?.mortgageType);

		const priority = getPriority(
			instruction?.properties?.[0]?.mortgagePriority,
		);

		const interestRateType =
			instruction?.mortgagePayment.interestRateType === 'FIXED'
				? InterestRateType.FIXED
				: InterestRateType.VARIABLE;

		const lenderMappedToCloser = this.mapCloserSyncMortgageeToCloser(
			transaction,
			instruction,
			transaction?.overview?.logoUrl,
		);

		const {
			mortgagePayment,
			mortgageOptions,
			mortgageLoanNumber,
			closingDate,
			trustAccount,
		} = instruction;
		const trustAccountOnInstructions = this.buildTrustAccount(trustAccount);

		const netAdvancedAmounts = buildMortgageFees(instruction);
		const insuranceAmountFlattened = mortgageOptions?.insuranceAmount
			?.toLowerCase()
			?.replace(/\s/g, '_');

		const _mortgage: Partial<MatterMortgage> = {
			actingForMortgagee: true,
			amount: mortgagePayment?.principalAmount,
			amountType: instruction?.amountType,
			amortization: mortgagePayment?.amortization?.toString(),
			assignmentOfRents: mortgageOptions?.assignmentOfRents,
			chargeType: instruction?.chargeType ?? null,
			creditApplicationNumber: instruction?.creditApplicationNumber,
			commitmentDate: this.tryGetDate(closingDate),
			defaultInsuranceProvider: mortgageOptions?.defaultInsuranceProvider,
			defaultInsuranceReferenceNumber:
				mortgageOptions?.defaultInsuranceReferenceNumber,
			finalReportEmail: instruction?.lender?.finalReportEmail,
			finalReportFaxNumber: instruction?.lender?.finalReportFaxNumber,
			firstPaymentDate: this.tryGetDate(mortgagePayment?.firstPaymentDate),
			firstPaymentDateAsReg:
				this.tryGetDate(mortgagePayment?.firstPaymentDateAsReg) ??
				mortgagePayment?.firstPaymentDateAsReg,
			insuranceAmount: InsuranceAmountMapping[insuranceAmountFlattened],
			instructionIssueDate: this.tryGetDate(instruction?.instructionIssueDate),
			interestAdjustmentDate: this.tryGetDate(
				mortgagePayment?.interestAdjustmentDate,
			),
			interestAdjustmentDateAsReg:
				this.tryGetDate(mortgagePayment?.interestAdjustmentDateAsReg) ??
				mortgagePayment?.interestAdjustmentDateAsReg,
			interestCalculated: mortgagePayment?.calculationPeriod?.toString(),
			interestCalculatedAsReg:
				mortgagePayment?.calculationPeriodAsReg?.toString(),
			interestRate: mortgagePayment?.interestRate,
			interestRateAsReg: mortgagePayment?.interestRateAsReg?.toString(),
			interestRateType,
			interestRateTypeAsReg: mortgagePayment?.interestRateType,
			lastPaymentDate: this.tryGetDate(mortgagePayment?.lastPaymentDate),
			lastPaymentDateAsReg: this.tryGetDate(
				mortgagePayment?.lastPaymentDateAsReg,
			),
			lenderComment: instruction?.note,
			lenderCollectsPropertyTaxes: mortgageOptions?.lenderCollectsPropertyTaxes,
			lenderLawyerId: instruction?.lawyer?.lenderLawyerId,
			lenderReferenceId: instruction?.lenderReferenceId,
			lenderContactName: instruction?.lender?.branchRepresentativeName,
			lenderContactPhoneNumber:
				instruction?.lender?.branchRepresentativePhoneNumber,
			lenderContactEmail: instruction?.lender?.branchRepresentativeEmail,
			maturityDate: this.tryGetDate(mortgagePayment?.maturityDate),
			maturityDateAsReg:
				this.tryGetDate(mortgagePayment?.maturityDateAsReg) ??
				mortgagePayment?.maturityDateAsReg,
			mms: false,
			netAdvanced: mortgagePayment?.netAdvanceAmount,
			netAdvancedOverride: mortgagePayment?.netAdvanceAmount,
			netAdvancedAmounts,
			originatingTransitNumber: instruction?.lender?.originatingTransitNumber,
			other: mortgagePayment?.insurancePortion,
			paymentDue: mortgagePayment?.paymentsDue?.toString(),
			paymentDueAsReg: mortgagePayment?.paymentsDueAsReg?.toString(),
			paymentFrequency: mortgagePayment?.paymentFrequency,
			paymentFrequencyAsReg: mortgagePayment?.paymentFrequencyAsReg,
			principalAndInterest: mortgagePayment?.principalAndInterestPayment,
			priority,
			referenceNumber: mortgageLoanNumber,
			regularPayments: mortgagePayment?.totalRegularPayment,
			regularPaymentsAsReg: isSet(mortgagePayment?.totalRegularPaymentAsReg)
				? `$${centsToDollars(mortgagePayment?.totalRegularPaymentAsReg).toString()}`
				: null,
			rffEmail: instruction?.lender?.rffEmail,
			rffFaxNumber: instruction?.lender?.rffFaxNumber,
			standardChargeTerm: mortgageOptions?.standardChargeTerm?.toString(),
			status: MortgageTransactionStatus.ACCEPTED,
			source: MatterMortgageInstructionSource.CLOSER_SYNC,
			syncFlowType:
				(instruction?.lender?.syncFlowType as SYNC_FLOW_TYPES) ??
				SYNC_FLOW_TYPES.FAX,
			taxes: mortgagePayment?.propertyTaxPortion,
			term: mortgagePayment?.mortgageTerm?.toString() ?? null,
			transactionType,
			type: MatterMortgageType.CLOSER_SYNC,
			fundingDetails: {
				trustAccount: useTrustAccountOnInstructions
					? trustAccountOnInstructions
					: null,
				trustAccountOnInstructions: trustAccountOnInstructions as TrustAccount,
				useTrustAccountOnInstructions: useTrustAccountOnInstructions,
				localBranch: null,
				accountNumber: null,
				bridgeFunding: null,
				type: useTrustAccountOnInstructions ? FundingType.WIRE_TRANSFER : null,
			},
			...lenderMappedToCloser,
		};

		return sanitizePropertyValues(_mortgage);
	};

	getTransactionType = (mortgageType: string): TransactionType => {
		switch (mortgageType) {
			case 'ARRANGED':
				return TransactionType.ARRANGED;
			default:
				return TransactionType.BRIDGE;
		}
	};

	/**
	 * Maps the mortgagee data from a transaction to the LenderData format used in Closer.
	 * @param instruction - The transaction data containing the mortgagee information.
	 * @returns The mapped LenderData object.
	 */
	mapCloserSyncMortgageeToCloser = (
		transaction: CloserSyncTransaction,
		instruction: CloserSyncInstruction,
		lenderUrl: string,
	): LenderData => {
		const id = createId();
		const mortgageeAddress = instruction?.mortgagee?.serviceAddress?.address;
		const address = convertLLCAddressToCloserAddress(mortgageeAddress);

		return {
			lenders: {
				[id]: {
					address,
					id,
					institutionNumber: instruction?.mortgagee?.institutionCode,
					logoUrl: lenderUrl,
					mortgageeSubType: 'institution',
					mortgageeType: MortgageeType.INSTITUTION,
					name: transaction?.lender?.nameEn,
					referenceId: transaction?.lender?.globalReferenceId,
					closerSyncId: instruction?.lender?.institutionId,
					type: FinancialInstitutionTypes.GENERAL_FINANCIAL_INSTITUTION,
					mortgageeName: instruction?.mortgagee?.name,
					requestForFundsContact: instruction?.lender?.requestForFundsContact,
					finalReportContact: instruction?.lender?.finalReportContact,
				},
			},
		};
	};
}
